import analytics from '../analytics';
import { parseResponse } from '../Api';
import nonCriticalException from '../exceptionLogger';
import ssFetch from '../ssFetch';
import { ShareTarget } from '../share';

type ReferralCreationRequestResponse = {
  code: string,
  reward_id: number,
  source: string,
  // will be a value w/ the format `available_client_referral_reward_{reward_id}`
  // NOT ShareTarget
};
export type LoggedInReferralCreationRequestPayload = {
  medium: string;
  campaign: string;
  sender_user_id: string;
  referral_uuid: string;
  provider_id?: string;
  source: ShareTarget;
  // NOT the same as value for ReferralCreationRequestResponse source
};
const LOGGED_IN_REFERRAL_REQUEST_URL = '/api/v1/referrals/client';
export async function makeReferralCreationRequest(
  payload: LoggedInReferralCreationRequestPayload,
): Promise<ReferralCreationRequestResponse> {
  analytics.track('referral_data_tracking_params_create_object_start', {
    payload,
  });
  const response = await ssFetch(
    LOGGED_IN_REFERRAL_REQUEST_URL, {
      method: 'POST',
      body: payload,
    },
  );
  if (response.status === 200) {
    throw new Error(`Reward ${response.status} already exists`);
  }

  return parseResponse<ReferralCreationRequestResponse>(
    response,
  ) as Promise<ReferralCreationRequestResponse>;
}

export async function sendLoggedInReferralCreationRequest(
  payload: LoggedInReferralCreationRequestPayload,
): Promise<{
    source: string,
    code: string,
    rewardId: number,
  } | null > {
  try {
    const { code, reward_id, source } = await makeReferralCreationRequest(payload);
    if (!!source && !!code && !!reward_id) {
      analytics.track('referral_data_tracking_params_create_object_success', { ...payload, reward_id });
      return {
        rewardId: reward_id,
        source,
        code,
      };
    }
  } catch (error) {
    nonCriticalException('referral_data_tracking_params_create_object_error',
      {
        error_message: error,
        payload,
      });
  }
  return null;
}

/**
 * This function is used by `useReferralLanding` to determine if
 * the page a client is on, contains valid url parameters/aka making
 * it a referral link and if so send back the correct payload.
 *
 * @param {Record<string, any>} routeParams
 *    object w/ values that
 *    might indicate the url the client is on,
 *    is a referral link
 * @param {ShareTarget} payloadTargetSource
 *   which page the recipient
 * @return {LoggedInReferralCreationRequestPayload | null}
 *   an object w/ the right format for `LOGGED_IN_REFERRAL_REQUEST_URL`
 *   or null.
 */
export function transformRouteParamsWithShareTarget(
  routeParams: Record<string, any>,
  payloadTargetSource: ShareTarget,
): LoggedInReferralCreationRequestPayload | null {
  const {
    utm_medium,
    utm_campaign,
    utm_term,
    referral_id,
  } = routeParams;

  if (utm_medium && utm_campaign && referral_id) {
    const sender_user_id = utm_campaign.split('_').slice(-1)[0];
    if (!utm_campaign.startsWith('sharing_referral_')) {
      return null;
    }
    return {
      source: payloadTargetSource,
      medium: utm_medium,
      campaign: utm_campaign,
      referral_uuid: referral_id,
      sender_user_id,
      ...((utm_term && utm_term.startsWith('provider_')) && {
        provider_id: utm_term.split('provider_').slice(-1)[0],
      }),
    };
  }
  return null;
}
